import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  readCosting,
  type AuditCost,
  type CostingReport,
} from "../api/client";
import { Callout } from "../components/Callout";
import { TextField } from "../components/TextField";

/**
 * M34 — per-audit costing, opened from screen 73.
 *
 * **There is no prototype screen for this.** `design/prototype/screens/` stops at 73 and the
 * costing screen is not in it, so nothing here is ported. The layout is assembled from the
 * idioms screens 70 and 73 already use — eyebrow, `sub`, `warnbar`, a `card` wrapping a
 * `tscroll` table — rather than invented, which is the closest this can get to the standing
 * rule that appearance comes from the prototype and nowhere else.
 *
 * **Two figures per audit, because one figure cannot answer the question this screen
 * exists for.** An audit that cost $2 to run and $0.10 a month thereafter is a different
 * commercial proposition from one that cost $3 once, and a plan built on a single number
 * cannot tell them apart. So the table carries the one-off and the monthly separately and
 * never adds them together.
 *
 * **The dates go through `TextField` like everything else**, which renders `type="text"`
 * and an `inputMode` and never `type="date"` or `type="number"` — those return null from
 * `selectionStart` and throw on `setSelectionRange`, so a re-rendering interface cannot
 * restore the caret and the text appears to type backwards.
 *
 * **What the screen must not do is imply more precision than the figures carry.** Model
 * spend is exact and stamped per call; the infrastructure share is an allocation that moves
 * when the denominator does; the retained figure is a monthly estimate divided across
 * audits holding a corpus. Each is labelled as what it is, and `unpriced_calls` is surfaced
 * rather than folded into a total — a call made before the price book covered its model is
 * *unpriced*, not free, and a screen that showed 0 there would read as a cheap audit.
 */
export function Costing({ onBack }: { onBack: () => void }) {
  const [report, setReport] = useState<CostingReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Defaulted in the browser rather than on the server, and that is the whole reason the
  // route has no default window: an administrator can see which period they are reading and
  // change it, instead of comparing a figure against a colleague's "last 30 days" that
  // covered different days.
  const [start, setStart] = useState(() => monthStart());
  const [end, setEnd] = useState(() => today());

  const load = useCallback(() => {
    setBusy(true);
    setError(null);
    readCosting({ start, end })
      .then((next) => {
        setReport(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setReport(null);
        setError(
          cause instanceof ApiError
            ? cause.message
            : "The costing could not be read.",
        );
      })
      .finally(() => setBusy(false));
  }, [start, end]);

  // Read once on arrival, then only when the administrator asks. Every read writes an
  // access event to each certification body's chain, so re-reading on every keystroke in
  // the date fields would fill their trails with reads nobody made.
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const audits = report?.audits ?? [];

  return (
    <>
      <button type="button" className="backlink" onClick={onBack}>
        &larr; Platform configuration
      </button>

      <div className="eyebrow">Complao admin</div>
      <h1>Costing</h1>
      <p className="sub" style={{ marginBottom: "18px" }}>
        What each audit consumed, across every certification body.
      </p>

      <Callout>
        <b>This read is recorded.</b> Every certification body whose audits appear gets{" "}
        <span className="mono">tenant.costing_read</span> on its own append-only chain,
        naming the references that were priced. It is a different entry from the one screen
        70 writes, because what it discloses is different &mdash; what an audit consumed
        rather than who it is for.
      </Callout>

      {error ? (
        <p className="problem" role="alert">
          {error}
        </p>
      ) : null}

      <div className="card">
        <h2>Period</h2>
        <p className="hint">
          The end is exclusive, as every date range on this platform is.
        </p>
        <div className="grid2">
          <TextField
            id="costing-from"
            label="From"
            value={start}
            onChange={setStart}
            placeholder="YYYY-MM-DD"
          />
          <TextField
            id="costing-to"
            label="To"
            value={end}
            onChange={setEnd}
            placeholder="YYYY-MM-DD"
          />
        </div>
        <div className="actrow">
          <button type="button" className="btn" onClick={load} disabled={busy}>
            {busy ? "Reading…" : "Read costing"}
          </button>
        </div>
      </div>

      {report && !report.infra_synced ? (
        <div className="warnbar">
          <b>No AWS cost data has been synced for this period.</b> The model figures below
          are exact and stamped per call. The infrastructure share is <i>absent</i>, not
          nought &mdash; nothing has told the platform what the instance cost, so no audit
          carries a share of it.
        </div>
      ) : null}

      {report && report.rates_incomplete ? (
        <div className="warnbar">
          <b>Some calls are unpriced.</b> They were made before the price book covered their
          model. An unpriced call is not a free one, so it is counted in its own column
          rather than added to a total as nought.
        </div>
      ) : null}

      {report ? <Totals report={report} /> : null}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div className="tscroll">
          <table>
            <thead>
              <tr>
                <th>Reference</th>
                <th>Certification body</th>
                <th>Client</th>
                <th style={{ textAlign: "right" }}>Model</th>
                <th style={{ textAlign: "right" }}>Infra share</th>
                <th style={{ textAlign: "right" }}>One-off</th>
                <th style={{ textAlign: "right" }}>Retained /mo</th>
                <th style={{ textAlign: "right" }}>Days</th>
                <th style={{ textAlign: "right" }}>Calls</th>
              </tr>
            </thead>
            <tbody>
              {audits.length === 0 ? (
                <tr>
                  <td colSpan={9} className="hint" style={{ padding: "18px" }}>
                    {busy
                      ? "Reading…"
                      : "No audit consumed anything in this period."}
                  </td>
                </tr>
              ) : (
                audits.map((row) => <Row key={row.audit_id} row={row} />)
              )}
            </tbody>
          </table>
        </div>
      </div>

      {report && report.services.length > 0 ? (
        <div className="card" style={{ marginTop: "18px" }}>
          <h2>What AWS billed</h2>
          <p className="hint">
            Cost Explorer&rsquo;s own service names, unmodified, so a figure here can be
            checked against the console by eye.
          </p>
          <div className="tscroll">
            <table>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Treatment</th>
                  <th style={{ textAlign: "right" }}>Cost</th>
                </tr>
              </thead>
              <tbody>
                {report.services.map((service) => (
                  <tr key={service.service}>
                    <td>{service.service}</td>
                    <td>
                      <span className={service.shared ? "pill p-wait" : "pill p-mute"}>
                        {service.shared ? "Divided by audit-day" : "Attributed by driver"}
                      </span>
                      {service.retained ? (
                        <span className="pill p-mute" style={{ marginLeft: "6px" }}>
                          Retained
                        </span>
                      ) : null}
                    </td>
                    <td style={{ textAlign: "right" }} className="mono">
                      {usd(service.cost_micros)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </>
  );
}

/**
 * The three numbers that explain the table, and the gap between two of them.
 *
 * `billed` and `allocated` are shown together on purpose. They will not agree, and the
 * difference is not an error: an instance is up whether or not an audit runs, so a month
 * with no audits still has a bill. That gap is the platform's own standing cost, and a
 * screen that showed only the allocation would hide it.
 */
function Totals({ report }: { report: CostingReport }) {
  const unallocated = report.billed_micros - report.allocated_micros;
  return (
    <div className="card">
      <h2>Totals</h2>
      <div className="kv">
        <div>
          <div className="hint">Billed by AWS</div>
          <div className="mono">{usd(report.billed_micros)}</div>
        </div>
        <div>
          <div className="hint">Allocated to audits</div>
          <div className="mono">{usd(report.allocated_micros)}</div>
        </div>
        <div>
          <div className="hint">Standing cost</div>
          <div className="mono">{usd(unallocated)}</div>
        </div>
        <div>
          <div className="hint">Audit-days</div>
          <div className="mono">{report.total_audit_days}</div>
        </div>
        <div>
          <div className="hint">Per audit-day</div>
          <div className="mono">{usd(report.shared_micros_per_audit_day)}</div>
        </div>
        <div>
          <div className="hint">Bodies read</div>
          <div className="mono">{report.bodies_read}</div>
        </div>
      </div>
      <p className="hint" style={{ marginTop: "12px" }}>
        Billed and allocated do not agree and are not meant to. The instance is up whether
        or not an audit runs, so the difference is the platform&rsquo;s own standing cost.
      </p>
    </div>
  );
}

function Row({ row }: { row: AuditCost }) {
  return (
    <tr>
      <td>
        <span className="mono">{row.reference}</span>
      </td>
      <td>{row.certification_body}</td>
      <td>{row.client_organisation}</td>
      <td style={{ textAlign: "right" }} className="mono">
        {usd(row.model_micros)}
      </td>
      <td style={{ textAlign: "right" }} className="mono">
        {usd(row.shared_infra_micros)}
      </td>
      <td style={{ textAlign: "right" }} className="mono">
        <b>{usd(row.one_off_micros)}</b>
      </td>
      <td style={{ textAlign: "right" }} className="mono">
        {usd(row.retained_micros_per_month)}
      </td>
      <td style={{ textAlign: "right" }} className="mono">
        {row.audit_days}
      </td>
      <td style={{ textAlign: "right" }} className="mono">
        {row.calls}
        {row.unpriced_calls > 0 ? (
          <>
            {" "}
            <span className="pill p-red" title="Made before the price book covered them">
              {row.unpriced_calls} unpriced
            </span>
          </>
        ) : null}
      </td>
    </tr>
  );
}

/**
 * Micros to dollars, and the only place the conversion happens.
 *
 * Every figure travels as an integer count of millionths of a dollar and becomes a decimal
 * here, once, at the last possible moment. Money carried as a float is money that disagrees
 * with itself after enough additions, which on a costing screen is the one defect nobody
 * would spot.
 *
 * Four decimal places rather than two: a single model call costs a fraction of a cent, and
 * a table rounding every row to $0.00 would be arithmetically true and completely useless.
 */
function usd(micros: number): string {
  return `$${(micros / 1_000_000).toFixed(4)}`;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthStart(): string {
  const now = new Date();
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}-01`;
}
